import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Icon } from '@iconify/react/dist/iconify.js';
import { deleteIntegrationsWithId, getAllIntegrations } from '../utils/api/integrationAPI';
import { convertToIndianTimeZone } from '../utils/utils';
import { toast } from 'sonner';
import ConfirmDeleteModal from './child/ConfirmDeleteModal';

const ViewAllUserLayer = () => {
    // Get dynamic URL param 
    const { id } = useParams();

    // to navigate to different page
    const navigate = useNavigate();

    // Integration data and pagination states
    const [integrationData, setIntegrationData] = useState([]);
    const [page, setPage] = useState(1);
    const [totalRecords, setTotalRecords] = useState(0); 
    const [numberOfResult, setNumberOfResult] = useState(10);
    const [showModal, setShowModal] = useState(false); 
    const [integrationToDelete, setIntegrationToDelete] = useState(null); 
    const [isLoading, setIsLoading] = useState(false); 
    const [lastFetchKey, setLastFetchKey] = useState(""); 

    // Memoized total pages
    const totalPages = useMemo(() => {
        const total = Math.ceil(totalRecords / numberOfResult);
        return total > 0 ? total : 1;
    }, [totalRecords, numberOfResult]);

    // Fetch integration data with memoized key to avoid refetching on same inputs
    const fetchData = useCallback(async () => {
        const currentKey = `${id}_${page}_${numberOfResult}`;
        if (currentKey === lastFetchKey) return;

        try {
            setIsLoading(true);
            const data = await getAllIntegrations();
            setIntegrationData(data["integrations"]);
            setTotalRecords(data["total"]);
            setLastFetchKey(currentKey); 
        } catch (error) {
            toast.error("Failed to fetch integrations");
        } finally {
            setIsLoading(false);
        }
    }, [id, page, numberOfResult, lastFetchKey]);

    // Initial and on-change effect for data
    useEffect(() => {
        fetchData();
    }, [fetchData]);

    // Set integration ID to delete
    const handleDeleteClick = (integrationId) => {
        setIntegrationToDelete(integrationId);
        setShowModal(true);
    };

    // Confirm delete and refetch data
    const handleConfirmDelete = async () => {
        try {
            const response = await deleteIntegrationsWithId(integrationToDelete);
            toast.success(response?.message || "Organisation deleted successfully.");
            setIntegrationToDelete(null);
            setShowModal(false);
            setLastFetchKey(""); 
            fetchData();
        } catch (error) {
            toast.error(error.message || "Error while deleting integration");
            console.error("Delete error:", error.message);
            setShowModal(false);
        }
    };

    // Cancel delete operation
    const handleCancelDelete = () => {
        setShowModal(false);
    };

    // handle edit operation
    const handleEdit = (integrationId) => {
        navigate(`/integration/edit/${integrationId}`)
    };


    return (
        <div className="col" style={{ height: 'auto' }}>
            <div className="card p-0 overflow-hidden position-relative radius-12">
                {/* Header: Search input and Add Integration button */}
                <div className="card-header border-bottom bg-base py-16 px-24 d-flex align-items-center flex-wrap gap-3 justify-content-between">
                    <div className="d-flex align-items-center flex-wrap gap-3">
                        <form className="navbar-search">
                            <input
                                type="text"
                                className="bg-base h-40-px w-auto"
                                name="search"
                                placeholder="Search"
                            />
                            <Icon icon="ion:search-outline" className="icon" />
                        </form>
                    </div>
                    <Link
                        to="/add-integration"
                        className="btn btn-primary text-sm btn-sm px-12 py-12 radius-8 d-flex align-items-center gap-2"
                    >
                        <Icon icon="ic:baseline-plus" className="icon text-xl line-height-1" />
                        Add New Integrations
                    </Link>
                </div>

                <div className="card-body p-24 pt-10">
                    {/* Table container */}
                    <div className="table-responsive scroll-sm">
                        <table className="table bordered-table sm-table mb-0">
                            <thead>
                                <tr>
                                    <th>ID</th>
                                    <th>Created At</th>
                                    <th>Name</th>
                                    <th>API Key</th>
                                    <th>Type</th>
                                    <th className="text-center">Status</th>
                                    <th className="text-center">Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {integrationData.map((item) => (
                                    <tr key={item.id}>
                                        <td>
                                            <div className="d-flex align-items-center gap-10">
                                                {item.id.length > 7 ? `${item.id.slice(0, 6)}...` : item.id}
                                            </div>
                                        </td>
                                        <td>{convertToIndianTimeZone(item.createdAt)}</td>
                                        <td>{item.name}</td>
                                        <td>
                                            {item.api_key
                                                ? "****" + item.api_key.slice(0, 4) + "****" + item.api_key.slice(-4)
                                                : "N/A"}
                                        </td>
                                        <td>{item.type}</td>
                                        <td className="text-center">
                                            {item.status ? (
                                                <span className="badge text-sm fw-semibold text-success-600 bg-success-100 px-20 py-9 radius-4 text-white">
                                                    Active
                                                </span>
                                            ) : (
                                                <span className="badge text-sm fw-semibold text-danger-600 bg-danger-100 px-20 py-9 radius-4 text-white">
                                                    InActive
                                                </span>
                                            )}
                                        </td>
                                        <td className="text-center">
                                            <div className="d-flex align-items-center gap-10 justify-content-center">
                                                
                                                <button type="button" onClick={() => handleEdit(item.id)} className="bg-success-focus text-success-600 bg-hover-success-200 fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle">
                                                    <Icon icon="lucide:edit" className="menu-icon" />
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => handleDeleteClick(item.id)}
                                                    className="remove-item-btn bg-danger-focus bg-hover-danger-200 text-danger-600 fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"
                                                >
                                                    <Icon icon="fluent:delete-24-regular" className="menu-icon" />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {/* Delete confirmation modal */}
                    {showModal && (
                        <ConfirmDeleteModal
                            handleConfirmDelete={handleConfirmDelete}
                            handleCancelDelete={handleCancelDelete}
                        />
                    )}

                    {/* Empty State */}
                    {integrationData.length === 0 && (
                        <div className="d-flex flex-column justify-content-center align-items-center" style={{ height: '52vh' }}>
                            <p className="text-secondary-light text-md mb-16">No integrations found</p>
                        </div>
                    )}

                    {/* Pagination */}
                    <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mt-24">
                        <span>
                            Showing 1 to {numberOfResult} of {totalRecords} entries
                        </span>
                        <ul className="pagination d-flex flex-wrap align-items-center gap-2 justify-content-center">
                            {/* Previous */}
                            <li className={`page-item ${page === 1 ? 'disabled' : ''}`}>
                                <button
                                    className="page-link bg-neutral-200 text-secondary-light fw-semibold radius-8 border-0 d-flex align-items-center justify-content-center h-32-px text-md"
                                    onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
                                >
                                    <Icon icon="ep:d-arrow-left" />
                                </button>
                            </li>

                            {/* Page numbers */}
                            {Array.from({ length: totalPages }, (_, i) => (
                                <li className="page-item" key={i}>
                                    <button
                                        className={`page-link fw-semibold radius-8 border-0 d-flex align-items-center justify-content-center h-32-px w-32-px text-md ${
                                            page === i + 1 ? 'bg-primary-600 text-white' : 'bg-neutral-200 text-secondary-light'
                                        }`}
                                        onClick={() => setPage(i + 1)}
                                    >
                                        {i + 1}
                                    </button>
                                </li>
                            ))}

                            {/* Next */}
                            <li className={`page-item ${page === totalPages ? 'disabled' : ''}`}>
                                <button
                                    className="page-link bg-neutral-200 text-secondary-light fw-semibold radius-8 border-0 d-flex align-items-center justify-content-center h-32-px text-md"
                                    onClick={() => setPage((prev) => Math.min(prev + 1, totalPages))}
                                >
                                    <Icon icon="ep:d-arrow-right" />
                                </button>
                            </li>
                        </ul>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ViewAllUserLayer;
